import type { ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { uniqWith } from 'ramda';
import { Apollo } from 'src/gql/generated';

export function useGetStorablePermissions(
  storableId: UUID | null,
  options?: Parameters<typeof Apollo.useGetStorablePermissionsQuery>[0]
): {
  loading: boolean;
  permissions: NonNullable<
    Apollo.GetStorablePermissionsQueryHookResult['data']
  >['storablePermissions'];
} {
  const { data, loading } = Apollo.useGetStorablePermissionsQuery({
    skip: !storableId,
    variables: { storableId: storableId ?? '' },
    ...options,
  });

  return { loading, permissions: data?.storablePermissions ?? [] };
}

export function useGetStorablePermissionsWithStorable(
  storableId: UUID,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetStorablePermissionsWithStorableQuery>[0]>,
    'variables'
  >
): {
  loading: boolean;
  storablePermissions: NonNullable<
    Apollo.GetStorablePermissionsWithStorableQueryHookResult['data']
  >['storablePermissions'];
} {
  const { data, loading } = Apollo.useGetStorablePermissionsWithStorableQuery({
    variables: { storableId },
    ...options,
  });

  return { loading, storablePermissions: data?.storablePermissions ?? [] };
}

/**
 * Upsert StorablePermissions.
 */
export function useUpsertStorablePermissions(): {
  loading: boolean;
  upsertStorablePermissions: (
    options: Parameters<Apollo.UpsertStorablePermissionsMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.UpsertStorablePermissionsMutation>>;
} {
  const [upsertStorablePermissions, { loading }] = Apollo.useUpsertStorablePermissionsMutation();

  return {
    loading,
    upsertStorablePermissions: options => {
      return upsertStorablePermissions({
        ...options,
        update: (cache, { data }) => {
          const newRefs =
            data?.upsertStorablePermissions.storablePermissions.flatMap(p => {
              const ref = cache.writeFragment({
                data: p,
                fragment: gql`
                  fragment NewPermission on StorablePermission {
                    __typename
                    id
                  }
                `,
              });
              return ref ? [ref] : [];
            }) ?? [];

          cache.modify({
            fields: {
              storablePermissions: (refs: readonly Reference[] = [], { readField }) => {
                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...refs, ...newRefs]
                );
              },
            },
          });
        },
      });
    },
  };
}

/**
 * Delete StorablePermission.
 */
export function useDeleteStorablePermission(): {
  deleteStorablePermission: (
    options: Parameters<Apollo.DeleteStorablePermissionMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.DeleteStorablePermissionMutation>>;
  loading: boolean;
} {
  const [deleteStorablePermission, { loading }] = Apollo.useDeleteStorablePermissionMutation();

  return {
    deleteStorablePermission: options => {
      return deleteStorablePermission({
        ...options,
        update: cache => {
          cache.modify({
            fields: {
              storablePermissions: (refs = [], { readField }) => {
                return (refs as Reference[]).filter(
                  (permissionRef: Reference) =>
                    readField('id', permissionRef) !== options?.variables?.permissionId
                );
              },
            },
          });
        },
      });
    },
    loading,
  };
}
